import React, { useState } from "react";
import { useMutation } from "react-query";
import {
  Alert,
  Badge,
  Button,
  Col,
  Container,
  Input,
  Row,
  Spinner,
  Table,
} from "reactstrap";
import { toast } from "react-toastify";
import {
  previewBatchCharges,
  runBatchCharges,
  getBatchChargeCsvUrl,
} from "../../query/queryFunctions";
import http from "../../service/http";

const STATUS_LABELS = {
  pay: { text: "Will charge card", color: "primary" },
  invoice: { text: "Will invoice", color: "info" },
  refund: { text: "Will refund", color: "primary" },
  paid: { text: "Charged", color: "success" },
  invoiced: { text: "Invoiced", color: "info" },
  invoiced_after_card_failure: { text: "Card failed, invoiced", color: "warning" },
  refunded: { text: "Refunded", color: "success" },
  not_found: { text: "Not found", color: "secondary" },
  ambiguous: { text: "Needs review", color: "warning" },
  duplicate: { text: "Duplicate", color: "secondary" },
  invalid: { text: "Invalid row", color: "danger" },
  failed: { text: "Failed", color: "danger" },
};

const money = (value) =>
  value === null || value === undefined ? "" : `$${Number(value).toFixed(2)}`;

const BatchCharges = () => {
  const [file, setFile] = useState(null);
  const [batch, setBatch] = useState(null);

  const previewMutation = useMutation(previewBatchCharges, {
    onSuccess: (data) => setBatch(data),
    onError: (error) => toast.error(error?.message || "Could not read the CSV"),
  });

  const runMutation = useMutation(runBatchCharges, {
    onSuccess: (data) => {
      setBatch(data);
      toast.success("Batch completed");
    },
    onError: (error) => toast.error(error?.message || "The batch failed"),
  });

  const rows = batch?.rows || [];
  const countOf = (...statuses) =>
    rows.filter((row) => statuses.includes(row.status)).length;
  const totalOf = (...statuses) =>
    rows
      .filter((row) => statuses.includes(row.status))
      .reduce((sum, row) => sum + Math.abs(row.amount || 0), 0);

  const isPreview = batch?.status === "preview";
  const isRunning = runMutation.isLoading;

  const handleUpload = () => {
    if (!file) {
      toast.error("Choose a CSV file first");
      return;
    }
    previewMutation.mutate(file);
  };

  const handleReset = () => {
    setBatch(null);
    setFile(null);
    previewMutation.reset();
    runMutation.reset();
  };

  const handleDownload = () =>
    http
      .download(getBatchChargeCsvUrl(batch._id), `batch-charges-${batch._id}.csv`)
      .catch(() => toast.error("Could not download the CSV"));

  return (
    <Container className="mt-5">
      <h4>Monthly Charges</h4>
      <p className="text-muted">
        Upload a CSV with the columns <code>name, email, amount, note</code>{" "}
        and an optional <code>group</code>. Rows sharing a group become one
        itemised invoice. Customers with a saved card are charged and get an
        itemised receipt; everyone else is sent an invoice due in 14 days. A
        negative amount is a refund: put the date of service in the note and the
        matching charge is refunded.{" "}
        <a href="/sample-batch-charges.csv" download>
          Download a sample CSV
        </a>
        .
      </p>

      {!batch && (
        <Row className="mb-4">
          <Col md={6}>
            <Input
              type="file"
              accept=".csv"
              onChange={(e) => setFile(e.target.files[0])}
            />
          </Col>
          <Col md={3}>
            <Button
              color="primary"
              onClick={handleUpload}
              disabled={previewMutation.isLoading}
            >
              {previewMutation.isLoading ? <Spinner size="sm" /> : "Preview"}
            </Button>
          </Col>
        </Row>
      )}

      {batch && (
        <>
          <Alert color={isPreview ? "warning" : "success"}>
            <strong>{batch.fileName}</strong> — {rows.length} rows.{" "}
            {isPreview ? (
              <>
                {countOf("pay")} to charge ({money(totalOf("pay"))}),{" "}
                {countOf("invoice")} to invoice ({money(totalOf("invoice"))}),{" "}
                {countOf("refund")} to refund ({money(totalOf("refund"))}),{" "}
                {countOf("not_found", "ambiguous", "duplicate", "invalid")} need
                attention. Nothing has been charged yet.
              </>
            ) : (
              <>
                {countOf("paid")} charged ({money(totalOf("paid"))}),{" "}
                {countOf("invoiced", "invoiced_after_card_failure")} invoiced (
                {money(totalOf("invoiced", "invoiced_after_card_failure"))}),{" "}
                {countOf("refunded")} refunded ({money(totalOf("refunded"))}),{" "}
                {countOf("not_found", "ambiguous", "duplicate", "invalid", "failed")}{" "}
                not processed.
              </>
            )}
          </Alert>

          <div className="mb-3">
            {isPreview && (
              <Button
                color="primary"
                className="mr-2"
                onClick={() => runMutation.mutate(batch._id)}
                disabled={isRunning || countOf("pay", "invoice", "refund") === 0}
              >
                {isRunning ? (
                  <Spinner size="sm" />
                ) : (
                  `Confirm: charge ${money(totalOf("pay"))}, invoice ${money(
                    totalOf("invoice")
                  )}, refund ${money(totalOf("refund"))}`
                )}
              </Button>
            )}
            {!isPreview && (
              <Button color="success" className="mr-2" onClick={handleDownload}>
                Download CSV
              </Button>
            )}
            <Button color="secondary" outline onClick={handleReset} disabled={isRunning}>
              {isPreview ? "Cancel" : "Upload another"}
            </Button>
          </div>

          <Table responsive hover bordered striped size="sm">
            <thead className="thead-dark">
              <tr>
                <th>Row</th>
                <th>Name</th>
                <th>Email</th>
                <th>Amount</th>
                <th>Note</th>
                <th>Group</th>
                <th>Status</th>
                <th>Details</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={row.rowNumber}>
                  <td>{row.rowNumber}</td>
                  <td>{row.name}</td>
                  <td>{row.email}</td>
                  <td>{money(row.amount)}</td>
                  <td>{row.note}</td>
                  <td>{row.group}</td>
                  <td>
                    <Badge color={STATUS_LABELS[row.status]?.color || "secondary"}>
                      {STATUS_LABELS[row.status]?.text || row.status}
                    </Badge>
                  </td>
                  <td>
                    {row.details}
                    {row.invoiceUrl && (
                      <>
                        {" "}
                        <a href={row.invoiceUrl} target="_blank" rel="noreferrer">
                          view invoice
                        </a>
                      </>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </>
      )}
    </Container>
  );
};

export default BatchCharges;
